import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import PlaceCard from "./PlaceCard";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Place {
    place_id: string;
    name: string;
    avg_rating: number;
    rate: number;
    address?: string;
    opening_hours?: string;
    description?: string;
    categories?: { id: number; name: string }[];
    images?: { file_url: string }[];
    review_count: number;
}

interface NormalizedPlace {
    id: string;
    name: string;
    rate: number;
    image_urls: string[];
    address?: string;
    opening_hours?: string;
    description?: string;
    review_count: number;
}

interface PlacesSectionProps {
    title: string;
    category: string;
    city?: string;
    hideIfEmpty?: boolean;

}

const PlaceCardSkeleton: React.FC = () => (
    <div className="w-64 flex-shrink-0 bg-white border border-gray rounded-lg overflow-hidden">
        <div className="w-full h-48 bg-gray-200 animate-pulse"></div>
        <div className="p-3">
            <div className="flex justify-between items-center mb-1">
                <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-8 animate-pulse"></div>
            </div>
            <div className="flex items-center h-4 mt-1">
                <div className="h-3 w-3 bg-gray-200 rounded-full ml-1 animate-pulse"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse"></div>
            </div>
            <div className="flex items-center h-4 mt-1">
                <div className="h-3 w-3 bg-gray-200 rounded-full ml-1 animate-pulse"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
            </div>
            <div className="mt-2 space-y-1">
                <div className="h-2 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-2 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-2 bg-gray-200 rounded w-2/3 animate-pulse"></div>
            </div>
        </div>
    </div>
);

const PlacesSection: React.FC<PlacesSectionProps> = ({ title, category, city, hideIfEmpty }) => {
    const desktopScrollRef = useRef<HTMLDivElement>(null);
    const mobileScrollRef = useRef<HTMLDivElement>(null);

    /* ---------------- VIEW ALL LINK ---------------- */
    const viewAllLink = city
        ? `/places/category/${encodeURIComponent(category)}?city=${encodeURIComponent(city)}`
        : `/places/category/${encodeURIComponent(category)}`;

    /* ---------------- FETCH ---------------- */
    const fetchPlaces = async (): Promise<NormalizedPlace[]> => {
        const params = new URLSearchParams({
            category,
            page: "1",
            page_size: "10",
        });
        if (city) params.append("city", city);

        const res = await fetch(`${BASE_URL}/api/places/?${params.toString()}`);
        if (!res.ok) throw new Error(`خطا در دریافت ${title}`);
        const json = await res.json();
        const dataArray: Place[] = Array.isArray(json) ? json : json.data || [];
        return dataArray.map((item) => ({
            id: item.place_id,
            name: item.name,
            rate: item.rate ?? 0,
            image_urls: item.images?.map((img) => img.file_url) || [],
            address: item.address,
            opening_hours: item.opening_hours,
            description: item.description,
            review_count: item.review_count,
        }));
    };

    const { data: places = [], isLoading, isError, error } = useQuery({
        queryKey: ["places", category, city ?? "all"],
        queryFn: fetchPlaces,
        staleTime: 5 * 60 * 1000,
    });

    if (hideIfEmpty && (isLoading || isError || places.length === 0)) {
        return null;
    }

    /* ---------------- SCROLL ---------------- */
    const scrollPrev = () =>
        desktopScrollRef.current?.scrollBy({ left: 600, behavior: "smooth" });
    const scrollNext = () =>
        desktopScrollRef.current?.scrollBy({ left: -600, behavior: "smooth" });

    /* ---------------- CONTENT ---------------- */
    const renderContent = () => {
        if (isLoading) {
            return Array.from({ length: 5 }).map((_, i) => (
                <PlaceCardSkeleton key={i} />
            ));
        }
        if (isError) {
            return (
                <div className="w-full text-center text-red-500 py-10 font-myYekanFaNum">
                    {(error as Error).message}
                </div>
            );
        }
        if (places.length === 0) {
            return (
                <div className="w-full text-center text-gray-400 py-10 font-myYekanFaNum">
                    موردی برای نمایش وجود ندارد
                </div>
            );
        }
        return places.map((place) => <PlaceCard key={place.id} place={place} />);
    };

    /* ---------------- RENDER ---------------- */
    return (
        <div className="py-1 px-0 desktop:px-16">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 desktop:px-20 tablet:px-20 px-[16px]">
                <h2 className="text-lg tablet:text-2xl desktop:text-3xl font-myYekanDemibold">
                    {title}
                </h2>
                <Link
                    to={viewAllLink}
                    className="flex items-center gap-1 text-[12px] text-gray-800 font-myYekanFaNumMedium"
                >
                    مشاهده همه
                    <ChevronLeftIcon className="h-4 w-4" />
                </Link>
            </div>

            {/* Mobile */}
            <div className="md:hidden">
                <div
                    ref={mobileScrollRef}
                    className="flex overflow-x-auto space-x-2 rtl:space-x-reverse px-2 pb-6 scrollbar-hide"
                >
                    {renderContent()}
                </div>
            </div>

            {/* Desktop */}
            <div className="hidden md:block relative px-12">
                <div className="flex items-center">
                    <button
                        onClick={scrollPrev}
                        className="w-10 h-10 flex items-center justify-center"
                    >
                        <ChevronRightIcon className="h-6 w-6" />
                    </button>
                    <div
                        ref={desktopScrollRef}
                        className="flex overflow-x-auto space-x-2 rtl:space-x-reverse pb-6 flex-1 scrollbar-hide"
                    >
                        {renderContent()}
                    </div>
                    <button
                        onClick={scrollNext}
                        className="w-10 h-10 flex items-center justify-center"
                    >
                        <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                </div>
            </div>

            <style>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default PlacesSection;